import { useCallback, useEffect, useRef, useState } from "react";

type SpeechRec = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult:
    | ((event: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void)
    | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function speechRecognitionCtor(): (new () => SpeechRec) | null {
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRec;
    webkitSpeechRecognition?: new () => SpeechRec;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function speechRecognitionAvailable(): boolean {
  return Boolean(speechRecognitionCtor());
}

export function useSpeechToText(onTranscript: (text: string) => void): {
  available: boolean;
  listening: boolean;
  toggle: () => void;
  stop: () => void;
} {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef<SpeechRec | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const available = speechRecognitionAvailable();

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const toggle = useCallback(() => {
    const ctor = speechRecognitionCtor();
    if (!ctor) return;
    if (recognitionRef.current) {
      stop();
      return;
    }
    const recognition = new ctor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = navigator.language || "en-US";
    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result?.isFinal) {
          const piece = result[0]?.transcript?.trim() ?? "";
          if (piece) onTranscriptRef.current(piece);
        }
      }
    };
    recognition.onerror = () => {
      recognitionRef.current = null;
      setListening(false);
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch {
      recognitionRef.current = null;
      setListening(false);
    }
  }, [stop]);

  return { available, listening, toggle, stop };
}
